import { useSiloWeb3 } from '@silo/web3';
import { DecoratedSilo, getCombinedDeposits, useQueryDecoratedSilo } from 'app/queries/decoratedSilo';
import { useMemo } from 'react';
import * as O from 'fp-ts/Option';
import { BigIntWithDecimals, ChecksumAddress, MerklRewardLegacy, getChecksumAddressOrThrow } from '@silo/types';
import { isUndefined } from '@silo/utils';

export const useSilo = (siloAddress: ChecksumAddress | null) => {
  const { protocolInstance } = useSiloWeb3();
  const contractsSilo = useQueryDecoratedSilo(siloAddress);

  return useMemo(() => {
    if (isUndefined(protocolInstance)) {
      return { status: 'loading', data: undefined, errors: undefined };
    }

    return transform({ contractsSilo });
  }, [contractsSilo, protocolInstance]);
};

interface TransformParams {
  contractsSilo: ReturnType<typeof useQueryDecoratedSilo>;
}

const transform = ({ contractsSilo }: TransformParams) => {
  if (contractsSilo.data) {
    return {
      status: 'contracts' as const,
      data: transformDecoratedSilo(contractsSilo.data),
    };
  }

  if (contractsSilo.status === 'loading') {
    return { status: 'loading' as const };
  }

  if (contractsSilo.error) {
    return { status: 'error' as const, errors: { contracts: contractsSilo.error } };
  }

  return { status: 'loading' as const };
};

export type SiloAsset = {
  assetAddress: ChecksumAddress;
  symbol: string;
  rawSymbol: string;
  name: string;
  decimals: number;
  liquidity: BigIntWithDecimals;
  borrowAPY: bigint;
  borrowRewardsApr: bigint;
  depositAPY: bigint;
  depositRewardsApr: bigint;
  isNative: boolean;
  utilization: bigint;
  priceInNative: bigint;
  priceProvider: O.Option<ChecksumAddress>;
  supply: bigint;
  merklRewards: MerklRewardLegacy[];
};

export type Silo = {
  siloAddress: ChecksumAddress;
  assets: {
    base: SiloAsset;
    bridge: SiloAsset[];
  };
};

const transformDecoratedSilo = (decoratedSilo: DecoratedSilo): Silo => {
  decoratedSilo.assets[0];
  const toAsset = (asset: DecoratedSilo['assets'][0]): SiloAsset => {
    return {
      assetAddress: getChecksumAddressOrThrow(asset.assetAddress),
      symbol: asset.symbol,
      rawSymbol: asset.rawSymbol,
      name: asset.name,
      decimals: asset.decimals,
      liquidity: {
        value: asset.liquidity.toBigInt(),
        decimals: asset.decimals,
      },
      borrowAPY: asset.borrowAPY.toBigInt(),
      borrowRewardsApr: asset.borrowRewardsApr.toBigInt(),
      depositAPY: asset.depositAPY.toBigInt(),
      depositRewardsApr: asset.depositRewardsApr.toBigInt(),
      isNative: asset.isNative,
      utilization: asset.utilization.toBigInt(),
      priceInNative: asset.priceInNative.toBigInt(),
      priceProvider:
        asset.priceProviders === '0x0000000000000000000000000000000000000000'
          ? O.none
          : O.some(getChecksumAddressOrThrow(asset.priceProviders)),
      supply: getCombinedDeposits(asset).toBigInt(),
      merklRewards: asset.lendingMerklRewards,
    };
  };

  return {
    siloAddress: getChecksumAddressOrThrow(decoratedSilo.siloAddress),
    assets: {
      base: toAsset(decoratedSilo.assets[0]),
      bridge: decoratedSilo.assets.slice(1).map(toAsset),
    },
  };
};
